import {
  AlertTriangle,
  ChevronLeft,
  Download,
  Lock,
  MoreHorizontal,
  RefreshCw,
  Trash2,
  Wrench,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useSourceWalletBalance } from "@/modules/dashboard/crypto-swaps/hooks/useSourceWalletBalance";
import { formatAmount } from "@/modules/dashboard/crypto-swaps/utils/cryptoSwapFormat";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { ColocaDropdownMenu } from "@/shared/components/overlays/ColocaDropdownMenu";
import { useStepUpRefusal } from "@/shared/hooks/useStepUpRefusal";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { UserType } from "@/shared/types/user.entity";
import { hasStepUpMethod } from "@/shared/utils/stepUp";
import { colocaToast } from "@/shared/utils/toast";
import { isTwoFactorCodeError, stepUpRefusal } from "@/shared/utils/twoFactorErrors";

import { CardLoadDialog } from "../components/card-load/CardLoadDialog";
import { CardBalanceModal, type BalanceModalMode } from "../components/CardBalanceModal";
import { CardDetailOverviewTab } from "../components/CardDetailOverviewTab";
import { CardDetailSidebar } from "../components/CardDetailSidebar";
import { CardMovements } from "../components/CardMovements";
import { CardTransactionDetailDrawer } from "../components/CardTransactionDetailDrawer";
import { CardCopTopUpDialog } from "../components/cop-top-up/CardCopTopUpDialog";
import { DeleteCardModal } from "../components/DeleteCardModal";
import { SendCardDataEmailModal } from "../components/SendCardDataEmailModal";
import { TwoFactorChallengeModal } from "../components/TwoFactorChallengeModal";
import { TwoFactorRequiredModal } from "../components/TwoFactorRequiredModal";
import { UpdateCardStatusModal } from "../components/UpdateCardStatusModal";
import { augmentCardWithMocks, type CardAugmented } from "../constants/cardMocks.constants";
import { MOVEMENTS_PAGE_SIZE } from "../constants/cards.constants";
import { useCardCopTopUpTerms } from "../hooks/useCardCopTopUpTerms";
import { useCardDetailSidebarData } from "../hooks/useCardDetailSidebarData";
import { useCardFundingOptions } from "../hooks/useCardFundingOptions";
import { useCardLiveActivity } from "../hooks/useCardLiveActivity";
import { useCardOperationsMaintenance } from "../hooks/useCardOperationsMaintenance";
import { CardsService } from "../services/cardsService";
import type { CardTransaction } from "../types/card-movements.types";
import type { CardDetail } from "../types/cards-api.types";
import { isCopTopUpOffered } from "../utils/cardCopTopUp";
import { normalizeStatus } from "../utils/cardHelpers";
import { appendMovementPage, leavesMovementGap, mergeLatestMovements } from "../utils/cardMovementHelpers";
import { CARD_MOVEMENT_PARAM } from "../utils/cardMovementLinks";
import { isIrreversibleStatus } from "../utils/cardStatus";

type DetailTabId = "overview" | "transactions" | "controls";

/** How long a movement that arrived by socket stays tinted in the lists. */
const FRESH_MOVEMENT_HIGHLIGHT_MS = 6000;
const NO_MOVEMENTS: ReadonlySet<string> = new Set();

/** History state of the entry a movement was opened into from this page, so closing it can step back. */
type MovementHistoryState = { movementOpenedHere?: boolean } | null;

const TABS: ReadonlyArray<{ id: DetailTabId; tKey: string }> = [
  { id: "overview", tKey: "card_detail_tab_overview" },
  { id: "transactions", tKey: "card_detail_tab_transactions" },
  { id: "controls", tKey: "card_detail_tab_controls" },
];

export const CardDetailPage = () => {
  const { t, activeLanguage } = useTranslations("cards");
  const { t: tGlobal } = useTranslations("global");
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const refusalMessage = useStepUpRefusal();
  const canViewSensitive = can("cards:sensitive:read");
  const canDeleteCard = can("cards:delete");
  const canRetryIssuance = can("cards:write");
  const params = useParams<"id">();
  const cardId = params.id!;
  const [searchParams, setSearchParams] = useSearchParams();

  const [card, setCard] = useState<CardDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Scoped to the card's own organization: the pause is per card provider, and
  // internal staff have no organization of their own to infer it from.
  const { movementsFrozen } = useCardOperationsMaintenance(card?.organization_id);

  const [viewUrl, setViewUrl] = useState<string | null>(null);
  const [isGeneratingUrl, setIsGeneratingUrl] = useState(false);

  // Read from the URL on every render, not once: a link to `?tab=transactions` (a card alert, say) must switch
  // the tab of the card already on screen too.
  const tabParam = searchParams.get("tab");
  const activeTab: DetailTabId =
    tabParam === "overview" || tabParam === "transactions" || tabParam === "controls" ? tabParam : "overview";

  const handleTabChange = useCallback(
    (next: DetailTabId) => {
      setSearchParams(
        (prev) => {
          if (next === "overview") {
            prev.delete("tab");
          } else {
            prev.set("tab", next);
          }
          return prev;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const [movements, setMovements] = useState<CardTransaction[]>([]);
  const [isLoadingMovements, setIsLoadingMovements] = useState(false);
  const [hasMoreMovements, setHasMoreMovements] = useState(false);
  // The open movement lives in the URL (`?movement=<id>`), so a link opens it directly — a corner alert, a
  // link someone pasted — and the back button closes it.
  const location = useLocation();
  const selectedTxnId = searchParams.get(CARD_MOVEMENT_PARAM);
  const openMovement = useCallback(
    (transactionId: string) =>
      setSearchParams(
        (prev) => {
          prev.set(CARD_MOVEMENT_PARAM, transactionId);
          return prev;
        },
        { state: { movementOpenedHere: true } satisfies MovementHistoryState },
      ),
    [setSearchParams],
  );
  // Opened from this page, the drawer closes by stepping back over the entry its opening pushed: replacing that
  // entry instead left two identical ones behind, and the next Back did nothing visible. Opened from a link,
  // there is no entry of ours to step back over, so the param is dropped in place.
  const closeMovement = useCallback(() => {
    if ((location.state as MovementHistoryState)?.movementOpenedHere) {
      navigate(-1);
      return;
    }
    setSearchParams(
      (prev) => {
        prev.delete(CARD_MOVEMENT_PARAM);
        return prev;
      },
      { replace: true },
    );
  }, [location.state, navigate, setSearchParams]);
  const [freshMovementIds, setFreshMovementIds] = useState<ReadonlySet<string>>(NO_MOVEMENTS);
  const [movementDetailRefreshKey, setMovementDetailRefreshKey] = useState(0);
  // Bumped by a live push only: refreshes the overview's figures without the sidebar's own reads (wallet
  // balance, transfer targets), which a purchase on this card does not change.
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);

  const [isUpdateStatusModalOpen, setIsUpdateStatusModalOpen] = useState(false);
  const [selectedNewStatus, setSelectedNewStatus] = useState<string | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const [balanceModalMode, setBalanceModalMode] = useState<BalanceModalMode | null>(null);
  const [statsRefreshKey, setStatsRefreshKey] = useState(0);

  const { walletAvailable, transferTargets, isLoadingTransferTargets, refreshWalletBalance } =
    useCardDetailSidebarData(cardId, statsRefreshKey);

  const [copTopUpOpen, setCopTopUpOpen] = useState(false);
  const [loadDialog, setLoadDialog] = useState<{ initialSource: "CRYPTO" | null } | null>(null);
  const canLoadFromCop = card?.integration_provider === "CRYPTO_MATE";
  // Read with the card, so an organization without the rollout never sees the option appear and vanish.
  const copTerms = useCardCopTopUpTerms(cardId, canLoadFromCop);
  // Every way in for money, answered for the card's organization (so staff see a client's card wallet).
  const funding = useCardFundingOptions(cardId, true, card?.organization_id ?? null);
  const fundingOptions = funding.options;
  // Unknown (still reading, or the read failed) counts as "no withdrawals": the flow then over-informs
  // rather than promising a withdrawal the organization may not have.
  const withdrawalsAllowed = fundingOptions?.withdrawals_allowed === true;
  const cryptoAvailable = !!fundingOptions?.sources.some(
    (source) => source.kind === "CRYPTO" && source.available,
  );
  const offersCop = canLoadFromCop && isCopTopUpOffered(copTerms.terms);
  const asksCopSource = offersCop && (loadDialog !== null || copTopUpOpen);
  const actsForOrganization = user?.user_type === UserType.INTERNAL ? (card?.organization_id ?? null) : null;
  const copBalance = useSourceWalletBalance("COP", actsForOrganization, asksCopSource);

  // Separate seq refs per fetcher — sharing one would let the second fetcher
  // invalidate the first and leave its spinner stuck on. `fetchCard` and `pollCardSilently` share
  // `cardSeqRef` on purpose: they read the same card, so the last answer wins whichever asked.
  const cardSeqRef = useRef(0);
  const movementsSeqRef = useRef(0);

  const aliasLabel = card?.alias?.trim() ?? "";
  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_tarjetas") },
      { label: tGlobal("sidebar_menu_cards"), to: "/app/cards/cards" },
      aliasLabel ? { label: aliasLabel } : { label: "", loading: true },
    ],
    [tGlobal, aliasLabel],
  );
  useAppShellBreadcrumb(breadcrumb);

  const refreshCardBalanceInBackground = useCallback(
    async (seq: number) => {
      try {
        const balanceRes = await CardsService.getCardBalance(cardId);
        if (seq !== cardSeqRef.current) return;
        const { current, pending } = balanceRes.data;
        const freshCurrent = Number.parseFloat(current.balance);
        if (!Number.isFinite(freshCurrent)) return;
        const freshPending = Number.parseFloat(pending.balance);
        setCard((prev) =>
          prev
            ? ({
                ...prev,
                balance: {
                  ...prev.balance,
                  [current.currency]: {
                    current: freshCurrent,
                    pending: Number.isFinite(freshPending) ? freshPending : 0,
                  },
                },
              } as CardDetail)
            : prev,
        );
      } catch {
        // Keep the cached balance if the live read fails.
      }
    },
    [cardId],
  );

  const fetchCard = useCallback(async () => {
    const seq = ++cardSeqRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const response = await CardsService.getCardById(cardId);
      if (seq !== cardSeqRef.current) return;
      setCard(response.data);
      // Render immediately with the cached balance, then patch it in the
      // background from the live read (pocket cards otherwise lag the pool).
      // Not awaited so it never delays first paint.
      void refreshCardBalanceInBackground(seq);
    } catch (err: unknown) {
      if (seq !== cardSeqRef.current) return;
      const errorMessage = HttpApiService.getErrorMessage(err) || t("card_detail_error_load");
      setError(errorMessage);
    } finally {
      if (seq === cardSeqRef.current) setIsLoading(false);
    }
  }, [cardId, t, refreshCardBalanceInBackground]);

  /**
   * The live refresh's read of the card: it replaces the card when it succeeds and does nothing at all when
   * it does not. Deliberately NOT `fetchCard`, which owns the page's `isLoading` and `error`, and the page
   * renders its error branch INSTEAD of everything else, so one failed read after a push would replace the
   * card with a red box. A card that is gone or no longer readable (404 / 403) is the exception: it falls to
   * the page's error as `fetchCard` would.
   */
  const pollCardSilently = useCallback(async () => {
    const seq = ++cardSeqRef.current;
    try {
      const response = await CardsService.getCardById(cardId);
      if (seq !== cardSeqRef.current) return;
      setCard(response.data);
      // Awaited, so a caller that reads the period figures next (a live push) reads them after the balance.
      await refreshCardBalanceInBackground(seq);
    } catch (err: unknown) {
      if (seq !== cardSeqRef.current) return;
      if (HttpApiService.isNotFoundError(err) || HttpApiService.isAccessDeniedError(err)) {
        setCard(null);
        setError(HttpApiService.getErrorMessage(err) || t("card_detail_error_load"));
      }
    }
  }, [cardId, t, refreshCardBalanceInBackground]);

  const [isRetryingIssuance, setIsRetryingIssuance] = useState(false);

  const handleRetryIssuance = useCallback(async () => {
    setIsRetryingIssuance(true);
    try {
      await CardsService.retryCardIssuance(cardId);
      colocaToast.success(t("card_retry_issuance_success"));
    } catch (err: unknown) {
      colocaToast.error(HttpApiService.getErrorMessage(err) || t("card_retry_issuance_error"));
    } finally {
      setIsRetryingIssuance(false);
    }
    // Also after a failure: the automatic retry may have issued it in the meantime.
    void fetchCard();
  }, [cardId, fetchCard, t]);

  const fetchMovements = useCallback(async () => {
    const seq = ++movementsSeqRef.current;
    setIsLoadingMovements(true);
    setMovements([]);
    setHasMoreMovements(false);
    try {
      const response = await CardsService.getCardTransactions(cardId, {
        skip: 0,
        limit: MOVEMENTS_PAGE_SIZE,
      });
      if (seq !== movementsSeqRef.current) return;
      const data = response.data.data ?? [];
      setMovements(data);
      setHasMoreMovements(response.data.hasMore ?? false);
    } catch (err: unknown) {
      if (seq !== movementsSeqRef.current) return;
      const msg = HttpApiService.getErrorMessage(err);
      colocaToast.error(msg);
    } finally {
      if (seq === movementsSeqRef.current) setIsLoadingMovements(false);
    }
  }, [cardId]);

  const loadMoreMovements = useCallback(async () => {
    if (isLoadingMovements || !hasMoreMovements) return;
    const seq = movementsSeqRef.current;
    setIsLoadingMovements(true);
    try {
      const response = await CardsService.getCardTransactions(cardId, {
        skip: movements.length,
        limit: MOVEMENTS_PAGE_SIZE,
      });
      // The list was read again meanwhile (another card, a refresh): this page belongs to the old one.
      if (seq !== movementsSeqRef.current) return;
      const data = response.data.data ?? [];
      setMovements((prev) => appendMovementPage(prev, data));
      setHasMoreMovements(response.data.hasMore ?? false);
    } catch (err: unknown) {
      const msg = HttpApiService.getErrorMessage(err) || t("card_detail_error_load_more_movements");
      colocaToast.error(msg);
    } finally {
      setIsLoadingMovements(false);
    }
  }, [cardId, movements.length, hasMoreMovements, isLoadingMovements, t]);

  useEffect(() => {
    fetchCard();
    fetchMovements();
  }, [fetchCard, fetchMovements]);

  /**
   * The first page read again and folded into what is on screen (see `mergeLatestMovements`), never a reset:
   * a live update must not drop the pages loaded with "load more" or flash the list empty. A failure keeps
   * the list as it is.
   */
  const movementsRef = useRef(movements);
  useEffect(() => {
    movementsRef.current = movements;
  }, [movements]);
  const hasCardRef = useRef(false);
  useEffect(() => {
    hasCardRef.current = card !== null;
  }, [card]);

  const refreshLatestMovements = useCallback(async () => {
    const seq = movementsSeqRef.current;
    try {
      const response = await CardsService.getCardTransactions(cardId, {
        skip: 0,
        limit: MOVEMENTS_PAGE_SIZE,
      });
      // A full read started meanwhile (another card, a manual refresh): its answer is the one that counts.
      if (seq !== movementsSeqRef.current) return;
      const latest = response.data.data ?? [];
      if (leavesMovementGap(movementsRef.current, latest, MOVEMENTS_PAGE_SIZE)) {
        setMovements(latest);
        setHasMoreMovements(response.data.hasMore ?? false);
        return;
      }
      setMovements((prev) => mergeLatestMovements(prev, latest));
    } catch {
      // The next push, a reconnection or a reload reads again.
    }
  }, [cardId]);

  // Something happened on this card while it is open (a purchase, a load, a deposit…): the movement shows up
  // in the lists, tinted for a moment, and the balance, the period figures and an open detail of that movement
  // follow. A reconnection (no ids) reads everything again, the open detail included.
  useCardLiveActivity(cardId, (movementIds) => {
    void refreshLatestMovements();
    // Not while the page's first read is out: that read is newer than the push anyway, and the poll would take
    // its sequence and leave the page on its spinner. The card read is the poll's silent one, so a failure never
    // paints the page red, and the figures are read after the balance.
    if (hasCardRef.current) {
      void pollCardSilently().then(() => setActivityRefreshKey((k) => k + 1));
    }
    if (selectedTxnId && (movementIds.size === 0 || movementIds.has(selectedTxnId))) {
      setMovementDetailRefreshKey((k) => k + 1);
    }
    if (movementIds.size > 0) setFreshMovementIds(movementIds);
  });

  useEffect(() => {
    if (freshMovementIds.size === 0) return;
    const timer = setTimeout(() => setFreshMovementIds(NO_MOVEMENTS), FRESH_MOVEMENT_HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [freshMovementIds]);

  const augmented: CardAugmented | null = useMemo(
    () => (card ? augmentCardWithMocks(card, 0) : null),
    [card],
  );

  const handleBack = useCallback(() => {
    navigate("/app/cards/cards");
  }, [navigate]);

  const [isTwoFactorModalOpen, setIsTwoFactorModalOpen] = useState(false);
  /** Which action sent the person to the enrolment notice, so a PIN created there carries that action on. */
  const pendingStepUpRef = useRef<"reveal" | "email" | "status" | null>(null);
  const stepUpReady = hasStepUpMethod(user);
  const [isTwoFactorChallengeOpen, setIsTwoFactorChallengeOpen] = useState(false);

  const [isEmailConfirmOpen, setIsEmailConfirmOpen] = useState(false);
  const [isEmailTwoFactorOpen, setIsEmailTwoFactorOpen] = useState(false);
  const [isSendingDataEmail, setIsSendingDataEmail] = useState(false);

  const recipientEmail = (card?.email ?? "").trim();

  const handleToggleReveal = useCallback(async () => {
    if (viewUrl) {
      setViewUrl(null);
      return;
    }
    if (!stepUpReady) {
      pendingStepUpRef.current = "reveal";
      setIsTwoFactorModalOpen(true);
      return;
    }
    setIsTwoFactorChallengeOpen(true);
  }, [viewUrl, stepUpReady]);

  const handleSubmitRevealChallenge = useCallback(
    async (code: string) => {
      setIsGeneratingUrl(true);
      try {
        const response = await CardsService.generateCardViewUrl(cardId, { twoFactorCode: code });
        setViewUrl(response.data.view_url);
      } catch (err: unknown) {
        if (isTwoFactorCodeError(err)) {
          throw stepUpRefusal(err, refusalMessage(err, t("two_factor_challenge_error_invalid_code")));
        }
        const message = HttpApiService.getErrorMessage(err) || t("card_detail_error_generate_url");
        colocaToast.error(message);
        throw err;
      } finally {
        setIsGeneratingUrl(false);
      }
    },
    [cardId, t, refusalMessage],
  );

  const handleGoToTwoFactorSettings = useCallback(() => {
    pendingStepUpRef.current = null;
    setSelectedNewStatus(null);
    setIsTwoFactorModalOpen(false);
    navigate("/app/settings?section=seguridad");
  }, [navigate]);

  const handleTwoFactorRequiredOpenChange = useCallback((open: boolean) => {
    setIsTwoFactorModalOpen(open);
    if (open) return;
    // Closed without a method: the status change that was waiting is dropped, as before.
    if (pendingStepUpRef.current === "status") setSelectedNewStatus(null);
    pendingStepUpRef.current = null;
  }, []);

  // A security PIN created in the notice: the action that asked for it goes on to its challenge.
  const handleTwoFactorEnrolled = useCallback(() => {
    const pending = pendingStepUpRef.current;
    pendingStepUpRef.current = null;
    setIsTwoFactorModalOpen(false);
    if (pending === "reveal") setIsTwoFactorChallengeOpen(true);
    else if (pending === "email") setIsEmailTwoFactorOpen(true);
    else if (pending === "status") setIsStatusTwoFactorOpen(true);
  }, []);

  const handleOpenEmailConfirm = useCallback(() => {
    if (!recipientEmail) {
      colocaToast.error(t("card_detail_email_error_no_email"));
      return;
    }
    setIsEmailConfirmOpen(true);
  }, [recipientEmail, t]);

  const handleConfirmEmailSend = useCallback(() => {
    setIsEmailConfirmOpen(false);
    if (!stepUpReady) {
      pendingStepUpRef.current = "email";
      setIsTwoFactorModalOpen(true);
      return;
    }
    setIsEmailTwoFactorOpen(true);
  }, [stepUpReady]);

  const handleSubmitEmailTwoFactor = useCallback(
    async (code: string) => {
      setIsSendingDataEmail(true);
      try {
        await CardsService.sendCardDataEmail(cardId, { twoFactorCode: code });
        colocaToast.success(t("card_detail_email_success"));
      } catch (err: unknown) {
        if (isTwoFactorCodeError(err)) {
          throw stepUpRefusal(err, refusalMessage(err, t("two_factor_challenge_error_invalid_code")));
        }
        const message = HttpApiService.getErrorMessage(err) || t("card_detail_email_error");
        colocaToast.error(message);
        throw err;
      } finally {
        setIsSendingDataEmail(false);
      }
    },
    [cardId, t, refusalMessage],
  );

  const handleToggleFreeze = useCallback(() => {
    if (!card) return;
    const status = normalizeStatus(card.status || card.card_status);
    const nextStatus = status === "FROZEN" ? "ACTIVE" : "FROZEN";
    setSelectedNewStatus(nextStatus);
    setIsUpdateStatusModalOpen(true);
  }, [card]);

  const handleDisableCard = useCallback(() => {
    if (!card) return;
    setSelectedNewStatus("DISABLED");
    setIsUpdateStatusModalOpen(true);
  }, [card]);

  const [isStatusTwoFactorOpen, setIsStatusTwoFactorOpen] = useState(false);

  const performStatusChange = useCallback(
    async (twoFactorCode?: string) => {
      if (!selectedNewStatus || !cardId) return;
      setIsUpdatingStatus(true);
      try {
        await CardsService.updateCardStatus(
          cardId,
          selectedNewStatus,
          twoFactorCode ? { twoFactorCode } : undefined,
        );
        setCard((prev) => (prev ? { ...prev, status: selectedNewStatus } : null));
        colocaToast.success(t("card_detail_status_update_success"));
        setIsUpdateStatusModalOpen(false);
        setSelectedNewStatus(null);
      } catch (err: unknown) {
        if (isTwoFactorCodeError(err)) {
          throw stepUpRefusal(err, refusalMessage(err, t("two_factor_challenge_error_invalid_code")));
        }
        const errorMessage = HttpApiService.getErrorMessage(err) || t("card_detail_status_update_error");
        colocaToast.error(errorMessage);
        setIsUpdateStatusModalOpen(false);
        setSelectedNewStatus(null);
      } finally {
        setIsUpdatingStatus(false);
      }
    },
    [selectedNewStatus, cardId, t, refusalMessage],
  );

  const handleConfirmStatusChange = useCallback(async () => {
    if (!selectedNewStatus || !cardId) return;
    // Terminal statuses are 2FA-gated server-side; run the challenge before
    // the call instead of failing it. Reversible toggles stay one-click.
    if (isIrreversibleStatus(selectedNewStatus)) {
      setIsUpdateStatusModalOpen(false);
      if (!stepUpReady) {
        // The status stays selected: a PIN created in the notice continues with this very change.
        pendingStepUpRef.current = "status";
        setIsTwoFactorModalOpen(true);
        return;
      }
      setIsStatusTwoFactorOpen(true);
      return;
    }
    await performStatusChange();
  }, [selectedNewStatus, cardId, stepUpReady, performStatusChange]);

  const handleSubmitStatusChallenge = useCallback(
    async (code: string) => {
      await performStatusChange(code);
    },
    [performStatusChange],
  );

  const handleCloseStatusModal = useCallback(() => {
    setIsUpdateStatusModalOpen(false);
    setSelectedNewStatus(null);
  }, []);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeletingCard, setIsDeletingCard] = useState(false);

  const handleConfirmDelete = useCallback(async () => {
    setIsDeletingCard(true);
    try {
      await CardsService.deleteCard(cardId);
      colocaToast.success(t("card_delete_success"));
      setIsDeleteModalOpen(false);
      navigate("/app/cards/cards");
    } catch (err: unknown) {
      colocaToast.error(HttpApiService.getErrorMessage(err) || t("card_delete_error"));
    } finally {
      setIsDeletingCard(false);
    }
  }, [cardId, navigate, t]);

  const currentStatus = useMemo(
    () => (card ? normalizeStatus(card.status || card.card_status) : null),
    [card],
  );

  const handleExport = useCallback(() => {
    navigate(`/app/cards/${cardId}/statement`);
  }, [navigate, cardId]);

  if (isLoading && !card) {
    return (
      <div className="flex items-center justify-center py-12 p-2">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <span className="text-sm text-muted-foreground">{t("card_detail_loading")}</span>
        </div>
      </div>
    );
  }

  if (error || !card || !augmented) {
    return (
      <div className="mx-auto w-full max-w-[1400px] space-y-4 p-2">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          {t("card_detail_back_to_list")}
        </button>
        <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {error || t("card_detail_error_load")}
        </div>
      </div>
    );
  }

  const last4 = card.last4 || card.last_4 || "0000";
  const pocket = augmented.pocketRef;
  const alias = card.alias?.trim() || `•••• ${last4}`;

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-6 p-2">
      <button
        type="button"
        onClick={handleBack}
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
        {t("card_detail_back_to_list")}
      </button>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-semibold leading-tight tracking-tight text-foreground">
            {alias}
          </h1>
          <p className="mt-1 truncate text-sm text-muted-foreground">
            <span className="font-mono">•••• {last4}</span>
            {pocket && (
              <>
                {" "}
                · <span style={{ color: pocket.color }}>{pocket.name}</span>
              </>
            )}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <ColocaButton variant="default" icon={Download} onClick={handleExport}>
            {t("card_detail_export")}
          </ColocaButton>
          {canDeleteCard && (
            <ColocaDropdownMenu
              align="end"
              trigger={
                <ColocaIconButton
                  icon={MoreHorizontal}
                  aria-label={t("card_detail_more_options")}
                  variant="default"
                />
              }
              items={[
                {
                  label: t("card_detail_menu_delete"),
                  icon: Trash2,
                  variant: "destructive",
                  disabled: isUpdatingStatus,
                  onClick: () => setIsDeleteModalOpen(true),
                },
              ]}
            />
          )}
        </div>
      </header>

      {movementsFrozen && (
        <ColocaAlert
          icon={Wrench}
          variant="warning"
          heading={t("card_maintenance_banner_title")}
          text={t("card_maintenance_banner_description")}
        />
      )}

      {currentStatus === "PENDING_REVIEW" && (
        <ColocaAlert
          icon={AlertTriangle}
          variant="warning"
          inline
          heading={t("card_retry_issuance_title")}
          text={t("card_retry_issuance_description")}
          actions={
            canRetryIssuance && (
              <ColocaButton
                variant="default"
                size="sm"
                icon={RefreshCw}
                loading={isRetryingIssuance}
                disabled={movementsFrozen}
                onClick={handleRetryIssuance}
              >
                {t("card_retry_issuance_action")}
              </ColocaButton>
            )
          }
        />
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[380px_1fr]">
        <CardDetailSidebar
          card={augmented}
          viewUrl={viewUrl}
          isGeneratingUrl={isGeneratingUrl}
          canViewSensitive={canViewSensitive}
          onToggleReveal={handleToggleReveal}
          onToggleFreeze={handleToggleFreeze}
          onDisable={handleDisableCard}
          isUpdatingStatus={isUpdatingStatus}
          onSendDataEmail={handleOpenEmailConfirm}
          isSendDataEmailDisabled={!recipientEmail}
        />

        <div className="min-w-0 space-y-6">
          <div className="flex items-center gap-0 overflow-x-auto overflow-y-hidden border-b border-border">
            {TABS.map((tab) => {
              const active = tab.id === activeTab;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => handleTabChange(tab.id)}
                  className={cn(
                    "relative inline-flex h-10 cursor-pointer items-center gap-1.5 whitespace-nowrap px-3 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    active ? "text-foreground" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {t(tab.tKey as never)}
                  {active && (
                    <span className="absolute -bottom-px left-2 right-2 h-[2px] rounded-t bg-primary" />
                  )}
                </button>
              );
            })}
          </div>

          {activeTab === "overview" && (
            <CardDetailOverviewTab
              card={augmented}
              refreshKey={statsRefreshKey + activityRefreshKey}
              onTopUp={() => {
                setLoadDialog({ initialSource: null });
                funding.reload();
                if (canLoadFromCop) copTerms.reload();
              }}
              onMove={() => setBalanceModalMode("move")}
              onWithdraw={withdrawalsAllowed ? () => setBalanceModalMode("withdraw") : undefined}
              onReceiveCrypto={cryptoAvailable ? () => setLoadDialog({ initialSource: "CRYPTO" }) : undefined}
              movementsDisabled={movementsFrozen}
              recentMovements={movements}
              isLoadingMovements={isLoadingMovements}
              onViewAllMovements={() => handleTabChange("transactions")}
              onMovementClick={openMovement}
              freshMovementIds={freshMovementIds}
            />
          )}

          {activeTab === "transactions" && (
            <CardMovements
              movements={movements}
              isLoading={isLoadingMovements}
              hasMore={hasMoreMovements}
              onLoadMore={loadMoreMovements}
              onRowClick={openMovement}
              highlightedIds={freshMovementIds}
              onRefresh={fetchMovements}
            />
          )}

          {activeTab === "controls" && (
            <ColocaEmpty
              icon={Lock}
              title={t("card_detail_tab_coming_soon_title")}
              description={t("card_detail_tab_coming_soon_description")}
            />
          )}
        </div>
      </div>

      <UpdateCardStatusModal
        isOpen={isUpdateStatusModalOpen}
        onClose={handleCloseStatusModal}
        onConfirm={handleConfirmStatusChange}
        currentStatus={currentStatus || undefined}
        newStatus={selectedNewStatus || undefined}
        isLoading={isUpdatingStatus}
      />

      <TwoFactorRequiredModal
        open={isTwoFactorModalOpen}
        onOpenChange={handleTwoFactorRequiredOpenChange}
        reason="view_card"
        onGoToSettings={handleGoToTwoFactorSettings}
        onEnrolled={handleTwoFactorEnrolled}
      />

      <TwoFactorChallengeModal
        open={isTwoFactorChallengeOpen}
        onOpenChange={setIsTwoFactorChallengeOpen}
        onSubmit={handleSubmitRevealChallenge}
      />

      <TwoFactorChallengeModal
        open={isStatusTwoFactorOpen}
        onOpenChange={(open) => {
          setIsStatusTwoFactorOpen(open);
          if (!open) setSelectedNewStatus(null);
        }}
        onSubmit={handleSubmitStatusChallenge}
      />

      <DeleteCardModal
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        cardName={alias}
        onConfirm={handleConfirmDelete}
        isLoading={isDeletingCard}
      />

      <SendCardDataEmailModal
        open={isEmailConfirmOpen}
        onOpenChange={setIsEmailConfirmOpen}
        recipientEmail={recipientEmail}
        isLoading={isSendingDataEmail}
        onConfirm={handleConfirmEmailSend}
        onCancel={() => setIsEmailConfirmOpen(false)}
      />

      <TwoFactorChallengeModal
        open={isEmailTwoFactorOpen}
        onOpenChange={setIsEmailTwoFactorOpen}
        onSubmit={handleSubmitEmailTwoFactor}
        title={t("card_detail_email_two_factor_title")}
        description={t("card_detail_email_two_factor_description")}
      />

      {card && copTopUpOpen && copTerms.terms?.available && (
        <CardCopTopUpDialog
          open={true}
          cardId={card.id}
          last4={card.last_4 || card.last4 || ""}
          terms={copTerms.terms}
          organizationId={actsForOrganization}
          balance={copBalance}
          withdrawalsAllowed={withdrawalsAllowed}
          timing={fundingOptions?.cop_timing ?? null}
          onClose={(placed) => {
            setCopTopUpOpen(false);
            if (!placed) return;
            copBalance.refresh();
            funding.reload();
            void Promise.allSettled([fetchCard(), refreshWalletBalance()]).then(() =>
              setStatsRefreshKey((k) => k + 1),
            );
          }}
        />
      )}

      {card && balanceModalMode && !movementsFrozen && (
        <CardBalanceModal
          isOpen={true}
          initialMode={balanceModalMode}
          card={card}
          withdrawalsAllowed={withdrawalsAllowed}
          // The card's organization wallet, not the viewer's: staff on a client's card see the client's.
          walletAvailable={
            fundingOptions?.card_wallet_balance != null
              ? Number(fundingOptions.card_wallet_balance)
              : walletAvailable
          }
          cardAvailable={augmented?.balanceMock ?? 0}
          transferTargets={transferTargets}
          isLoadingTransferTargets={isLoadingTransferTargets}
          onClose={() => setBalanceModalMode(null)}
          onSuccess={async () => {
            // Re-fetch the card (fresh card.balance → withdraw cap) and the org
            // wallet (fresh topup cap) so both modals show the settled balance,
            // not the value captured at page load. Stats key refreshes the hero.
            // The movement it made goes on the lists too.
            void refreshLatestMovements();
            await Promise.allSettled([fetchCard(), refreshWalletBalance()]);
            funding.reload();
            setStatsRefreshKey((k) => k + 1);
          }}
        />
      )}

      {/* Same boundary as the dialogs it hands over to: no movements while operations are frozen. */}
      {card && loadDialog && !movementsFrozen && (
        <CardLoadDialog
          open={true}
          card={card}
          last4={card.last_4 || card.last4 || ""}
          options={fundingOptions}
          isLoading={funding.isLoading}
          hasError={funding.hasError}
          onRetry={funding.reload}
          copBalance={
            copBalance.available
              ? `${formatAmount(copBalance.available, activeLanguage, { min: 0, max: 0 })} COP`
              : null
          }
          initialSource={loadDialog.initialSource}
          onClose={() => setLoadDialog(null)}
          onChooseCop={() => {
            setLoadDialog(null);
            copTerms.reload();
            setCopTopUpOpen(true);
          }}
          onChooseWallet={() => {
            setLoadDialog(null);
            setBalanceModalMode("load");
          }}
        />
      )}

      <CardTransactionDetailDrawer
        open={!!selectedTxnId}
        cardId={cardId}
        transactionId={selectedTxnId}
        onOpenChange={(isOpen) => {
          if (!isOpen) closeMovement();
        }}
        refreshKey={movementDetailRefreshKey}
      />
    </div>
  );
};
